/**
 * Text folding for the filter field in `Select.vue`.
 *
 * WHY THIS IS NOT `label.toLowerCase().includes(query.toLowerCase())`.
 *
 * The list this exists for is the language picker, whose forty labels are written in their own
 * scripts — `Français`, `Português`, `Čeština`, `العربية`. Lowercasing alone means an agent on a
 * plain keyboard cannot reach `Français` by typing `fran`, because `ç` is not `c`. And the
 * product's primary language is Arabic, where the SAME word is routinely spelled several ways:
 * `إنجليزية` / `انجليزية` differ only by a hamza the writer may or may not type, and a search
 * that treats them as different words fails the majority of the users here.
 *
 * So both scripts are folded to a comparison form before matching. Nothing is displayed from
 * this function — it only decides what matches — so folding costs no fidelity on screen.
 */

// Latin combining marks, left over after NFD decomposes `ç` into `c` + cedilla.
const LATIN_MARKS = /[\u0300-\u036f]/g;

// Arabic marks that are optional in ordinary writing and therefore cannot be required to match:
// the tashkeel block, the dagger alef, the hamza/madda marks NFD leaves behind, and the tatweel
// (a pure typographic stretch character, U+0640, which carries no sound at all).
const ARABIC_MARKS = /[\u064b-\u065f\u0670\u0640]/g;

// Letter variants a writer chooses between without changing the word.
const ALEF = /[\u0622\u0623\u0625\u0671]/g; // آ أ إ ٱ  → ا
const YEH = /[\u0649\u06cc]/g; // ى ی      → ي
const TEH_MARBUTA = /\u0629/g; // ة        → ه
const KAF = /\u06a9/g; // ک (Persian)      → ك

// Arabic-Indic and Extended Arabic-Indic digits. An agent typing `2` should reach a label
// written `٢` — inbox and campaign names in this product mix both.
const ARABIC_DIGITS = /[\u0660-\u0669\u06f0-\u06f9]/g;
const foldDigit = digit => {
  const code = digit.charCodeAt(0);
  const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
  return String((code - base) % 10);
};

/**
 * @param {*} text anything; non-strings are coerced, null/undefined fold to ''.
 * @returns {string} the comparison form — lower case, unaccented, Arabic-normalised.
 */
export const foldForSearch = text => {
  if (text === null || text === undefined) return '';

  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(LATIN_MARKS, '')
    .replace(ARABIC_MARKS, '')
    .replace(ALEF, 'ا')
    .replace(YEH, 'ي')
    .replace(TEH_MARBUTA, 'ه')
    .replace(KAF, 'ك')
    .replace(ARABIC_DIGITS, foldDigit)
    .trim();
};

/**
 * Substring rather than prefix: an agent looking for "Egypt" in a label that reads
 * "Arabic (Egypt)" has no way to know which word the list was sorted on.
 *
 * The ORDER of the options is never touched. Re-ranking on every keystroke moves rows out from
 * under a cursor that is already travelling toward one of them, and the list is short enough
 * once filtered that ranking buys nothing.
 *
 * @param {Array<{label: *, value: *, description: *}>} options
 * @param {string} query
 * @returns {Array} the options that match, in their original order.
 */
export const filterOptions = (options = [], query = '') => {
  const needle = foldForSearch(query);
  if (!needle) return options;

  return options.filter(option => {
    // The value is searched too: locale codes (`ar`, `pt_BR`) are how half of these lists are
    // actually identified, and they are frequently not in the visible label.
    const haystack = `${foldForSearch(option.label)} ${foldForSearch(
      option.description
    )} ${foldForSearch(option.value)}`;
    return haystack.includes(needle);
  });
};
